import LessonPlayer from './LessonPlayer'
import type { VideoTracking } from '../media/useVideoAnalytics'
import type { LessonDetail } from '../../lib/content'
import { useCanvasReserved } from '../../contexts/drawing'

/**
 * The lesson recordings are captured at 1660×1080, so a lesson that has one
 * reserves that exact frame.
 */
export const LESSON_VIDEO_ASPECT = '1660 / 1080'

/**
 * The video area at the top of an (unlocked) lesson. Renders the player when the
 * lesson has a recording.
 *
 * Without one it renders nothing. It used to render a blank frame of identical
 * dimensions so that switching between lessons didn't shift the layout — but
 * only 5 of 415 lessons have a recording, so in practice that opened almost
 * every lesson with a large empty box, which is what a customer reported as the
 * page "looking odd". The blank frame comes back only when it is actually being
 * used: while the owner has the drawing toolbar open, or on a page that already
 * carries ink drawn over that space, which would otherwise land on the content
 * that shifted up to fill it.
 */
export default function LessonVideoSlot({
  video,
  track,
}: {
  video: LessonDetail['video']
  track?: VideoTracking
}) {
  const canvasReserved = useCanvasReserved()
  if (video) {
    return <LessonPlayer video={video} aspectRatio={LESSON_VIDEO_ASPECT} track={track} />
  }
  if (!canvasReserved) return null
  return (
    <div
      style={{ aspectRatio: LESSON_VIDEO_ASPECT }}
      className="mb-4 w-full rounded-smooth-lg border border-border-subtle bg-brand-navy/[0.03]"
      aria-hidden="true"
    />
  )
}
